import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { isChunkReloadPending } from '../lib/chunkRecovery';

/**
 * ErrorBoundary superior del árbol de rutas (LAZY-CHUNK-RECOVERY-P0).
 *
 * Sin él, cualquier error de render —un chunk lazy que no carga o un throw en
 * una página— hace que React desmonte la raíz y deja `#root` vacío: pantalla en
 * blanco sin salida. Con él se muestra una pantalla recuperable con «Reintentar».
 *
 * - Si hay una recarga de recuperación en curso (chunkRecovery), muestra solo el
 *   spinner: la página se va a recargar y no tiene sentido enseñar un error.
 * - «Reintentar» recarga la página y NO limpia la marca de recuperación: un
 *   reintento manual fallido vuelve aquí en vez de encadenar recargas.
 * - Cambiar de ruta (p. ej. «atrás») restablece el boundary.
 */

type Props = { children: ReactNode; resetKey: string };
type State = { error: Error | null };

class AppErrorBoundaryInner extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[app] error de render capturado por AppErrorBoundary:', error, info.componentStack);
  }

  componentDidUpdate(prev: Props) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;

    if (isChunkReloadPending()) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-white">
          <div
            className="w-8 h-8 rounded-full border-2 border-gray-200 animate-spin"
            style={{ borderTopColor: '#3C2285' }}
            role="status"
            aria-label="Cargando"
          />
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-6">
        <div role="alert" className="w-full max-w-xs text-center">
          <div className="text-[26px] font-semibold tracking-tight" style={{ color: '#3C2285' }}>
            LucyCare
          </div>
          <p className="mt-3.5 mb-5 text-[15px] leading-relaxed text-gray-600">
            No pudimos cargar esta pantalla. Revisa tu conexión e inténtalo de nuevo.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="block w-full min-h-[44px] rounded-full px-5 py-3 text-[15px] font-semibold text-white"
            style={{ background: '#3C2285' }}
          >
            Reintentar
          </button>
          <a href="/" className="mt-4 inline-block text-sm text-gray-500 underline">
            Ir al inicio
          </a>
        </div>
      </div>
    );
  }
}

export default function AppErrorBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return <AppErrorBoundaryInner resetKey={pathname}>{children}</AppErrorBoundaryInner>;
}
